import React, { useState, useEffect } from 'react';
import { Plus, Search, Filter, ChevronRight, ChevronDown, Building2, Users, DollarSign, Target, TrendingUp, Calendar, Edit, Trash2 } from 'lucide-react';
import { dataProvider } from '@/lib/dataProvider';

const OrganizationPage: React.FC = () => {
  const [departments, setDepartments] = useState<any[]>([]);
  const [selectedYear, setSelectedYear] = useState('2025');
  const [selectedQuarter, setSelectedQuarter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    // Load organization data from dataProvider
    const orgData = dataProvider.getOrganization();
    setDepartments(orgData);
  }, []);

  const toggleExpand = (deptId: string) => {
    const updateExpanded = (depts: any[]): any[] => {
      return depts.map((dept: any) => {
        if (dept.id === deptId) {
          return { ...dept, isExpanded: !dept.isExpanded };
        }
        if (dept.children) {
          return { ...dept, children: updateExpanded(dept.children) };
        }
        return dept;
      });
    };
    setDepartments(updateExpanded(departments));
  };

  const renderDepartmentTree = (depts: any[], level: number = 0) => {
    return depts.map((dept: any) => (
      <div key={dept.id}>
        <div className={`flex items-center p-3 hover:bg-gray-50 border-l-4 ${
          level === 0 ? 'border-blue-500 bg-blue-50' :
          level === 1 ? 'border-green-500' : 'border-gray-300'
        }`} style={{ marginLeft: `${level * 24}px` }}>
          <button
            onClick={() => toggleExpand(dept.id)}
            className="mr-2 p-1 hover:bg-gray-200 rounded"
          >
            {dept.children && dept.children.length > 0 ? (
              dept.isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />
            ) : (
              <div className="w-4 h-4" />
            )}
          </button>
          
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center mr-3 ${
            level === 0 ? 'bg-blue-100' :
            level === 1 ? 'bg-green-100' : 'bg-gray-100'
          }`}>
            <Building2 className={`w-4 h-4 ${
              level === 0 ? 'text-blue-600' :
              level === 1 ? 'text-green-600' : 'text-gray-600'
            }`} />
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium text-gray-900">{dept.name}</h3>
                <div className="flex items-center text-sm text-gray-500 space-x-4">
                  <span>Mã: {dept.code}</span>
                  <span>Quản lý: {dept.managerName}</span>
                  <span className="flex items-center">
                    <Users className="w-4 h-4 mr-1" />
                    {dept.employeeCount} người
                  </span>
                </div>
              </div>
              
              <div className="flex items-center space-x-6">
                <div className="text-right">
                  <div className="text-sm font-medium text-gray-900">
                    {(dept.budget.spent / 1000000).toFixed(0)}M / {(dept.budget.annual / 1000000).toFixed(0)}M
                  </div>
                  <div className="text-xs text-gray-500">Ngân sách</div>
                </div>
                
                <div className="text-right">
                  <div className="text-sm font-medium text-gray-900">
                    {dept.kpis.length} KPIs
                  </div>
                  <div className="text-xs text-gray-500">
                    {dept.kpis.filter((k: any) => k.current >= k.target).length}/{dept.kpis.length} đạt
                  </div>
                </div>
                
                <div className="flex space-x-1">
                  <button className="p-1 text-gray-400 hover:text-gray-600">
                    <Edit className="w-4 h-4" />
                  </button>
                  {level > 0 && (
                    <button className="p-1 text-gray-400 hover:text-red-600">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {dept.isExpanded && dept.children && dept.children.length > 0 && (
          <div>
            {renderDepartmentTree(dept.children, level + 1)}
          </div>
        )}
      </div>
    ));
  };

  const getAllDepartments = (depts: any[]): any[] => {
    let result: any[] = [];
    depts.forEach((dept: any) => {
      result.push(dept);
      if (dept.children) {
        result = result.concat(getAllDepartments(dept.children));
      }
    });
    return result;
  };

  const allDepts = getAllDepartments(departments);
  const totalBudget = allDepts.reduce((sum: any, d: any) => sum + d.budget.annual, 0);
  const totalSpent = allDepts.reduce((sum: any, d: any) => sum + d.budget.spent, 0);
  const totalEmployees = allDepts.reduce((sum: any, d: any) => sum + d.employeeCount, 0);
  const totalKPIs = allDepts.reduce((sum: any, d: any) => sum + d.kpis.length, 0);
  const achievedKPIs = allDepts.reduce((sum: any, d: any) => sum + d.kpis.filter((k: any) => k.current >= k.target).length, 0);

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Quản lý Tổ chức</h1>
          <p className="text-gray-600">Cấu trúc tổ chức, ngân sách đào tạo và KPI theo đơn vị</p>
        </div>
        <button className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700">
          <Plus className="w-4 h-4" />
          Thêm đơn vị
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
        <div className="flex gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Tìm kiếm đơn vị..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="2025">2025</option>
            <option value="2023">2023</option>
          </select>
          <select
            value={selectedQuarter}
            onChange={(e) => setSelectedQuarter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">Cả năm</option>
            <option value="q1">Quý 1</option>
            <option value="q2">Quý 2</option>
            <option value="q3">Quý 3</option>
            <option value="q4">Quý 4</option>
          </select>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Building2 className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-600">Tổng nhân viên</p>
              <p className="text-2xl font-bold text-gray-900">{totalEmployees}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-600">Ngân sách năm</p>
              <p className="text-2xl font-bold text-gray-900">{(totalBudget / 1000000000).toFixed(1)}B</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-600">Đã sử dụng</p>
              <p className="text-2xl font-bold text-gray-900">{((totalSpent / totalBudget) * 100).toFixed(1)}%</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <Target className="w-6 h-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-600">KPI đạt</p>
              <p className="text-2xl font-bold text-gray-900">{achievedKPIs}/{totalKPIs}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Organization Tree */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Cấu trúc tổ chức</h2>
        </div>
        <div className="divide-y divide-gray-200">
          {renderDepartmentTree(departments)}
        </div>
      </div>
    </div>
  );
};

export default OrganizationPage; 